// ================================================================
//  ApiCollectionsDialogController
// ================================================================

angular.module('bizmetryApp').controller('ApiCollectionsDialogController', function (
    $scope,
    $mdDialog,
    $timeout,
    ApiCollectionService,
    ApiCollectionTree,
    ApiCollectionViewer,
    dialogService,
    sessionInfo
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    // ---- Search ----
    ctrl.searchTerm = '';

    // ---- Sort ----
    ctrl.sortOptions = [
        { label: 'Name A → Z', value: 'name_asc', field: 'collection_name', dir: 'ASC' },
        { label: 'Name Z → A', value: 'name_desc', field: 'collection_name', dir: 'DESC' },
        { label: 'Created (newest)', value: 'created_desc', field: 'created_ts', dir: 'DESC' },
        { label: 'Created (oldest)', value: 'created_asc', field: 'created_ts', dir: 'ASC' }
    ];
    ctrl.selectedSort = ctrl.sortOptions[0];

    // ---- Pagination ----
    ctrl.pageSizeOptions = [5, 10, 25, 50];
    ctrl.pageSize = 10;
    ctrl.currentPage = 0;
    ctrl.totalPages = 0;
    ctrl.totalElements = 0;
    ctrl.visibleCollections = [];

    // ---- UI state ----
    ctrl.isLoading = false;
    ctrl.error = null;
    ctrl.confirmDeleteMap = {};

    // Hand-rolled tooltip for the actions column instead of Angular
    // Material's own <md-tooltip> — md-dialog applies a CSS transform to
    // its content wrapper for its open/close animation, which becomes the
    // containing block for md-tooltip's internal fixed-positioning and
    // makes it render as a near-invisible, mispositioned sliver (same root
    // cause documented for the Roles & Permissions dialog in
    // roles-tab-controller.js). Same fix: one shared element portaled to
    // <body> via the existing rtBodyPortal directive, positioned in JS
    // from the trigger's getBoundingClientRect().
    ctrl.tooltipVisible  = false;
    ctrl.tooltipText     = '';
    ctrl.tooltipPosition = { left: 0, top: 0 };

    const TOOLTIP_MAX_WIDTH = 260;
    const TOOLTIP_MARGIN    = 8;

    ctrl.showTooltip = function (text, $event) {
        const triggerEl = $event && ($event.currentTarget || $event.target);
        if (!triggerEl || !triggerEl.getBoundingClientRect || !text) return;

        const rect = triggerEl.getBoundingClientRect();
        let left = Math.min(rect.left, window.innerWidth - TOOLTIP_MAX_WIDTH - TOOLTIP_MARGIN);
        left = Math.max(left, TOOLTIP_MARGIN);

        ctrl.tooltipText = text;
        ctrl.tooltipPosition = { left: left, top: rect.top - TOOLTIP_MARGIN };
        ctrl.tooltipVisible = true;
    };

    ctrl.hideTooltip = function () {
        ctrl.tooltipVisible = false;
    };

    ctrl.getTooltipStyle = function () {
        const pos = ctrl.tooltipPosition || {};
        return { left: pos.left + 'px', top: pos.top + 'px' };
    };

    let searchDebounce = null;
    let _deleteTimers = {};

    var TRANSITION_MAP = {
        'DRAFT':          [{ label: 'Submit for Review', icon: 'send',         target: 'PENDING_REVIEW', css: 'btn-tr-submit'  }],
        'PENDING_REVIEW': [{ label: 'Approve',           icon: 'check_circle', target: 'APPROVED',       css: 'btn-tr-approve' },
                           { label: 'Reject',            icon: 'cancel',       target: 'REJECTED',       css: 'btn-tr-reject'  }],
        'APPROVED':       [{ label: 'Publish',           icon: 'publish',      target: 'PUBLISHED',      css: 'btn-tr-publish' }],
        'PUBLISHED':      [{ label: 'Retire',            icon: 'archive',      target: 'RETIRED',        css: 'btn-tr-retire'  }],
        'REJECTED':       [{ label: 'Reopen as Draft',   icon: 'undo',         target: 'DRAFT',          css: 'btn-tr-reopen'  }],
        'RETIRED':        []
    };

    ctrl.getTransitions = function (status) {
        return TRANSITION_MAP[status] || [];
    };

    // ================================================================
    // INIT
    // ================================================================

    ctrl.$onInit = function () {
        ctrl.fetch();
    };

    // ================================================================
    // CORE FETCH
    // ================================================================

    // isLoading blocks the whole content area with the big centered spinner —
    // reserved for the very first fetch, when there's nothing on screen yet
    // to keep showing. Every later fetch (search/sort/page/page-size change)
    // uses isRefreshing instead: the current list/empty state stays mounted
    // and visible (just dimmed) so the dialog never blanks out or resizes
    // between keystrokes — only the data swaps once the response lands.
    ctrl.isRefreshing  = false;
    ctrl.hasLoadedOnce = false;

    ctrl.fetch = function () {
        if (ctrl.hasLoadedOnce) {
            ctrl.isRefreshing = true;
        } else {
            ctrl.isLoading = true;
        }
        ctrl.error = null;

        ApiCollectionService.getAll(sessionInfo.accountId, {
            page:      ctrl.currentPage,
            size:      ctrl.pageSize,
            search:    ctrl.searchTerm || undefined,
            sortField: ctrl.selectedSort.field,
            sortDir:   ctrl.selectedSort.dir,
            groupByFamily: true,
            includeReferences: false
        })
        .then(function (response) {
            if (response.status === 204) {
                ctrl.visibleCollections = [];
                ctrl.totalElements = 0;
                ctrl.totalPages = 1;
                _buildTree();
                return;
            }
            const data = response.data;
            ctrl.visibleCollections = data.content       || [];
            ctrl.totalElements      = data.totalElements || 0;
            ctrl.totalPages         = data.totalPages    || 1;
            _buildTree();

            if (ctrl.currentPage >= ctrl.totalPages) {
                ctrl.currentPage = Math.max(0, ctrl.totalPages - 1);
            }
        })
        .catch(function (err) {
            ctrl.error = (err.data && err.data.message) || 'Unexpected error loading collections.';
        })
        .finally(function () {
            ctrl.isLoading    = false;
            ctrl.isRefreshing = false;
            ctrl.hasLoadedOnce = true;
        });
    };

    // ================================================================
    // VERSION TREE
    // ================================================================
    // The list is paged by family (every version sharing a collection name,
    // see group_by_family) and shown as a tree (see ApiCollectionTree): each
    // version hangs from the version it derives from. parent_collection_id
    // comes resolved by the backend, which also uses it to cascade deletes.

    ctrl.treeRows    = [];   // every row, pre-order: { col, depth, parent, children, childCount, descendantCount }
    ctrl.visibleRows = [];   // rows whose ancestors are all expanded
    ctrl.expanded    = {};   // collection_id → true

    function _buildTree() {
        ctrl.treeRows = ApiCollectionTree.build(ctrl.visibleCollections);
        // Searching expands everything so every match is visible.
        if (ctrl.searchTerm) {
            ctrl.treeRows.forEach(function (r) { if (r.childCount) { ctrl.expanded[r.col.collection_id] = true; } });
        }
        _refreshVisibleRows();
    }

    function _refreshVisibleRows() {
        ctrl.visibleRows = ApiCollectionTree.visibleRows(ctrl.treeRows, ctrl.expanded);
    }

    ctrl.toggleExpanded = function (row, $event) {
        if ($event) { $event.stopPropagation(); }
        if (!row.childCount) { return; }
        ctrl.expanded[row.col.collection_id] = !ctrl.expanded[row.col.collection_id];
        _refreshVisibleRows();
    };

    // A family can have several base versions (e.g. 1.6.0 and 2.0.0), so
    // count distinct collection names, not tree roots.
    ctrl.familyCount = function () {
        var names = {};
        ctrl.visibleCollections.forEach(function (c) { names[c.collection_name] = true; });
        return Object.keys(names).length;
    };

    // ================================================================
    // SEARCH
    // ================================================================

    ctrl.onSearchChange = function () {
        if (searchDebounce) $timeout.cancel(searchDebounce);
        searchDebounce = $timeout(function () {
            ctrl.currentPage = 0;
            ctrl.fetch();
        }, 350);
    };

    ctrl.clearSearch = function () {
        ctrl.searchTerm  = '';
        ctrl.currentPage = 0;
        ctrl.fetch();
    };

    // ================================================================
    // SORT
    // ================================================================

    ctrl.onSortChange = function () {
        ctrl.currentPage = 0;
        ctrl.fetch();
    };

    // ================================================================
    // PAGINATION
    // ================================================================

    ctrl.setPageSize = function () {
        ctrl.currentPage = 0;
        ctrl.fetch();
    };

    ctrl.goToPage = function (page) {
        if (page < 0 || page >= ctrl.totalPages) return;
        ctrl.currentPage = page;
        ctrl.fetch();
    };

    ctrl.prevPage = function () { ctrl.goToPage(ctrl.currentPage - 1); };
    ctrl.nextPage = function () { ctrl.goToPage(ctrl.currentPage + 1); };

    // ================================================================
    // CREATE
    // ================================================================

    ctrl.createCollection = function () {
        $mdDialog.show({
            controller:          'CreateApiCollectionDialogController',
            controllerAs:        'ctrl',
            templateUrl:         'home/main-menu/api-collection/create-new/create-api-collection-dialog.html',
            parent:               angular.element(document.body),
            clickOutsideToClose: false,
            escapeToClose:       true,
            fullscreen:          false,
            multiple:            true,
            locals:              { sessionInfo: sessionInfo }
        }).then(function (result) {
            if (result && result.created) {
                ctrl.currentPage = 0;
                ctrl.fetch();
            }
        }).catch(angular.noop);
    };

    // ================================================================
    // DETAIL DIALOG
    // ================================================================

    ctrl.openDetails = function (collection) {
        ApiCollectionViewer.openDetails(collection.collection_id);
    };

    // ================================================================
    // STATUS TRANSITION
    // ================================================================

    // Retiring a version still linked to a template that isn't RETIRED is
    // blocked backend-side too (see ApiCollectionService#validatePublishedPatchRules) —
    // this just keeps the UI from letting the user attempt it.
    function _activeTemplates(collection) {
        return (collection.used_by_templates || []).filter(function (t) {
            return t.template_status !== 'RETIRED';
        });
    }

    ctrl.isTransitionBlocked = function (collection, targetStatus) {
        return targetStatus === 'RETIRED' && _activeTemplates(collection).length > 0;
    };

    /**
     * PATCH returns the full ApiCollectionDTO, but list cards are summaries:
     * operations_count / used_by_templates only exist there. Merge onto the card
     * instead of replacing it, and drop the heavy swagger/operations the list
     * never uses.
     */
    function _mergeIntoCard(card, full) {
        full = full || {};
        var merged = angular.extend({}, card, full);
        if (Array.isArray(full.operations)) {
            merged.operations_count = full.operations.length;
        }
        delete merged.operations;
        delete merged.swagger;
        return merged;
    }

    ctrl.transitionStatus = function (collection, targetStatus, $event) {
        if ($event) $event.stopPropagation();

        if (ctrl.isTransitionBlocked(collection, targetStatus)) return;

        ApiCollectionService.patch(collection.collection_id, { status: targetStatus })
            .then(function (updated) {
                var idx = ctrl.visibleCollections.findIndex(function (c) {
                    return c.collection_id === collection.collection_id;
                });
                if (idx !== -1) {
                    ctrl.visibleCollections[idx] = _mergeIntoCard(ctrl.visibleCollections[idx], updated);
                    _buildTree();
                }
            })
            .catch(function (err) {
                const msg = (err.data && err.data.message) || 'Status transition failed.';
                console.error('❌ [ApiCollections] transitionStatus error:', msg);
                dialogService.showErrorDialog(
                    'Status Transition Failed',
                    dialogService.formatErrorMessage({
                        error:     (err.data && err.data.error)     || 'Transition Error',
                        message:   msg,
                        path:      (err.data && err.data.path)      || '/v1/api/bizmetry/api-collection/' + collection.collection_id,
                        timestamp: (err.data && err.data.timestamp) || new Date().toISOString()
                    })
                );
            });
    };

    // ================================================================
    // EDIT
    // ================================================================

    ctrl.editCollection = function (collection, $event) {
        if ($event) $event.stopPropagation();

        ApiCollectionViewer.edit(collection.collection_id, sessionInfo).then(function (result) {
            // Edit of a PUBLISHED collection created a new version: reload the page.
            if (result && result.forked) {
                ctrl.fetch();
                return;
            }
            if (result && result.updated) {
                var idx = ctrl.visibleCollections.findIndex(function (c) {
                    return c.collection_id === collection.collection_id;
                });
                if (idx !== -1) {
                    ctrl.visibleCollections[idx] = _mergeIntoCard(ctrl.visibleCollections[idx], result.collection);
                    _buildTree();
                }
            }
        });
    };

    // ================================================================
    // DELETE
    // ================================================================

    ctrl.isEditable = ApiCollectionViewer.isEditable;

    ctrl.isDeletable = function (status) {
        return status !== 'PUBLISHED';
    };

    // Biz events use API collections through their template version, so a
    // template link is what makes a version in use.
    ctrl.isUsedByTemplate = function (collection) {
        return !!(collection.used_by_templates && collection.used_by_templates.length);
    };

    // Only the first few entries are listed — the tooltip is a small fixed-width box.
    const TEMPLATES_TOOLTIP_LIMIT = 5;

    function _templateList(templates) {
        const shown = templates.slice(0, TEMPLATES_TOOLTIP_LIMIT).map(function (t) {
            return (t.template_name || 'template') + (t.template_version ? ' v' + t.template_version : '');
        });
        const remaining = templates.length - shown.length;
        return shown.join(', ') + (remaining > 0 ? ', +' + remaining + ' more' : '');
    }

    // Deleting a version cascades to every version derived from it, so it's
    // blocked when anything in its subtree can't be deleted: linked to a
    // template, or (for derived versions) PUBLISHED.
    function _descendantRows(row) {
        var out = [];
        (row.children || []).forEach(function (child) {
            out.push(child);
            out = out.concat(_descendantRows(child));
        });
        return out;
    }

    ctrl.getDeleteBlockers = function (row) {
        function usedBy(col) {
            return 'v' + col.api_version + ' is used by template ' + _templateList(col.used_by_templates);
        }
        var blockers = ctrl.isUsedByTemplate(row.col) ? [usedBy(row.col)] : [];
        _descendantRows(row).forEach(function (d) {
            if (ctrl.isUsedByTemplate(d.col)) {
                blockers.push(usedBy(d.col));
            } else if (d.col.status === 'PUBLISHED') {
                blockers.push('v' + d.col.api_version + ' is PUBLISHED');
            }
        });
        return blockers;
    };

    ctrl.isDeleteBlocked = function (row) {
        return ctrl.getDeleteBlockers(row).length > 0;
    };

    ctrl.getDeleteTooltipText = function (row) {
        var blockers = ctrl.getDeleteBlockers(row);
        if (blockers.length) {
            return 'Cannot delete: ' + blockers.join('; ');
        }
        if (ctrl.confirmDeleteMap[row.col.collection_id]) return 'Click again to confirm deletion';
        return row.descendantCount
            ? 'Delete this version and ' + row.descendantCount + ' derived version' + (row.descendantCount === 1 ? '' : 's')
            : 'Delete collection';
    };

    ctrl.getTransitionTooltipText = function (collection, tr) {
        if (ctrl.isTransitionBlocked(collection, tr.target)) {
            return 'Cannot retire: used by template ' + _templateList(_activeTemplates(collection)) +
                '. Remove it from the template or retire the template first.';
        }
        // Transition buttons are icon-only (compact Actions column): the tooltip names the action.
        return tr.label;
    };

    ctrl.deleteCollection = function (row, $event) {
        if ($event) { $event.stopPropagation(); $event.preventDefault(); }

        if (ctrl.isDeleteBlocked(row)) return;
        var collection = row.col;

        const id = collection.collection_id;

        if (!ctrl.confirmDeleteMap[id]) {
            ctrl.confirmDeleteMap[id] = true;
            if (_deleteTimers[id]) $timeout.cancel(_deleteTimers[id]);
            _deleteTimers[id] = $timeout(function () {
                ctrl.confirmDeleteMap[id] = false;
                delete _deleteTimers[id];
            }, 3000);
            return;
        }

        if (_deleteTimers[id]) {
            $timeout.cancel(_deleteTimers[id]);
            delete _deleteTimers[id];
        }
        ctrl.confirmDeleteMap[id] = false;

        var derived = _descendantRows(row).map(function (d) { return 'v' + d.col.api_version; });
        var message = 'You are about to permanently delete <strong>' + collection.collection_name +
            ' v' + collection.api_version + '</strong>';
        if (derived.length) {
            message += ' and the ' + derived.length + ' version' + (derived.length === 1 ? '' : 's') +
                ' derived from it (<strong>' + derived.join(', ') + '</strong>)';
        }
        message += '. This action cannot be undone.';

        dialogService.showConfirmationDialog(
            'Delete Collection',
            message,
            collection.collection_name,
            true
        ).then(function (decision) {
            if (decision !== 'confirmed') return;

            ApiCollectionService.delete(id)
                .then(function () {
                    ctrl.currentPage = 0;
                    ctrl.fetch();
                })
                .catch(function (err) {
                    dialogService.showErrorDialog(
                        'Delete Failed',
                        dialogService.formatErrorMessage({
                            error:     (err.data && err.data.error)     || 'Delete Error',
                            message:   (err.data && err.data.message)   || 'Could not delete the collection. Please try again.',
                            path:      (err.data && err.data.path)      || '/v1/api/bizmetry/api-collection/' + id,
                            timestamp: (err.data && err.data.timestamp) || new Date().toISOString()
                        })
                    );
                });
        });
    };

    // ================================================================
    // CLOSE
    // ================================================================

    ctrl.close = function () {
        $mdDialog.hide();
    };
});


// ================================================================
//  ApiCollectionDetailDialogController
// ================================================================

angular.module('bizmetryApp').controller('ApiCollectionDetailDialogController', function (
    $scope,
    $mdDialog,
    collection,
    canEdit
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    ctrl.collection = collection;
    ctrl.canEdit = canEdit;

    // Same contract summary the Edit dialog shows (see EditApiCollectionDialogController#_refreshSummary).
    ctrl.sourceType = collection.swagger_source ? collection.swagger_source.source_type : null;
    ctrl.operationsCount = Array.isArray(collection.operations)
        ? collection.operations.length : (collection.operations_count || 0);
    ctrl.pathsCount = collection.swagger && collection.swagger.paths
        ? Object.keys(collection.swagger.paths).length : 0;

    ctrl.openSwagger = function () {
        if (!ctrl.collection.swagger) return;
        $mdDialog.show({
            templateUrl:         'home/main-menu/api-collection/swagger-viewer-dialog.html',
            controller:          'SwaggerViewerDialogController',
            controllerAs:        'ctrl',
            parent:               angular.element(document.body),
            clickOutsideToClose: true,
            escapeToClose:       true,
            fullscreen:          true,
            multiple:            true,
            locals:              { collection: ctrl.collection }
        }).catch(angular.noop);
    };

    // ApiCollectionViewer opens the Edit dialog once this one is closed.
    ctrl.edit = function () { $mdDialog.hide({ edit: true }); };

    ctrl.close = function () { $mdDialog.hide(); };
});


// ================================================================
//  SwaggerViewerDialogController
// ================================================================

angular.module('bizmetryApp').controller('SwaggerViewerDialogController', function (
    $scope,
    $mdDialog,
    $timeout,
    collection
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    ctrl.collection = collection;

    function loadSwaggerUI() {
        if (window.SwaggerUIBundle) { renderSwagger(); return; }

        const cssId = 'swagger-ui-css';
        if (!document.getElementById(cssId)) {
            const link = document.createElement('link');
            link.id   = cssId;
            link.rel  = 'stylesheet';
            link.href = 'https://unpkg.com/swagger-ui-dist@5/swagger-ui.css';
            document.head.appendChild(link);
        }

        const script    = document.createElement('script');
        script.src      = 'https://unpkg.com/swagger-ui-dist@5/swagger-ui-bundle.js';
        script.onload   = renderSwagger;
        document.head.appendChild(script);
    }

    function renderSwagger() {
        $timeout(function () {
            window.SwaggerUIBundle({
                spec:                     collection.swagger,
                domNode:                  document.getElementById('swagger-ui-container'),
                presets:                  [window.SwaggerUIBundle.presets.apis, window.SwaggerUIBundle.SwaggerUIStandalonePreset],
                layout:                   'BaseLayout',
                deepLinking:              false,
                displayRequestDuration:   true,
                defaultModelsExpandDepth: -1,
                tryItOutEnabled:          false
            });
        }, 0);
    }

    $timeout(loadSwaggerUI, 150);

    ctrl.close = function () { $mdDialog.hide(); };
});


// ================================================================
//  Filters
// ================================================================

angular.module('bizmetryApp').filter('bmStatusLabel', function () {
    const LABELS = {
        'DRAFT':          'Draft',
        'PENDING_REVIEW': 'Pending Review',
        'APPROVED':       'Approved',
        'PUBLISHED':      'Published',
        'REJECTED':       'Rejected',
        'RETIRED':        'Retired'
    };
    return function (status) { return LABELS[status] || status || '—'; };
});

angular.module('bizmetryApp').filter('bmRelativeTime', function () {
    return function (epochMs) {
        if (!epochMs) return '—';
        const diff  = Date.now() - epochMs;
        const sec   = Math.floor(diff / 1000);
        const min   = Math.floor(sec / 60);
        const hours = Math.floor(min / 60);
        const days  = Math.floor(hours / 24);
        if (sec < 60)   return 'just now';
        if (min < 60)   return min + 'm ago';
        if (hours < 24) return hours + 'h ago';
        if (days < 7)   return days + 'd ago';
        return new Date(epochMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
});